// Thin wrapper around @capacitor/push-notifications.
//
// All calls are guarded by Capacitor.isNativePlatform(): on web (and in the
// CRA dev/build), the plugin is never loaded and every function is a safe
// no-op, so the same codebase builds and runs unchanged on web, iOS, Android.
// The plugin talks to APNs (iOS) and FCM (Android) — a genuinely native
// capability the web app cannot provide.
import { Capacitor } from '@capacitor/core';

let pluginPromise = null;

// Lazy-load the native plugin only on a real device. Returns null on web.
async function getPlugin() {
  if (!Capacitor.isNativePlatform()) return null;
  if (!pluginPromise) {
    pluginPromise = import('@capacitor/push-notifications').then((m) => m.PushNotifications);
  }
  return pluginPromise;
}

export function isPushSupported() {
  return Capacitor.isNativePlatform();
}

// Request OS permission and register with APNs/FCM. Resolves to the device
// token string. Throws an Error with code 'permission-denied' if the user
// declined, or a generic error on registration failure/timeout.
export async function requestAndRegister() {
  const PushNotifications = await getPlugin();
  if (!PushNotifications) return null;

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') {
    const err = new Error('Push permission not granted');
    err.code = 'permission-denied';
    throw err;
  }

  // The device token arrives asynchronously via the 'registration' event.
  return new Promise((resolve, reject) => {
    let settled = false;
    let regHandle;
    let errHandle;
    const cleanup = () => {
      try { regHandle?.remove(); } catch { /* noop */ }
      try { errHandle?.remove(); } catch { /* noop */ }
    };

    Promise.all([
      PushNotifications.addListener('registration', (token) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(token.value);
      }),
      PushNotifications.addListener('registrationError', (e) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error(e?.error || 'Push registration failed'));
      }),
    ]).then(([reg, err]) => {
      regHandle = reg;
      errHandle = err;
      return PushNotifications.register();
    }).catch((e) => {
      if (!settled) { settled = true; cleanup(); reject(e); }
    });

    setTimeout(() => {
      if (!settled) { settled = true; cleanup(); reject(new Error('Push registration timed out')); }
    }, 15000);
  });
}

// Remove all listeners. (APNs/FCM have no true client-side "unsubscribe"; the
// server stops sending once the token is removed from Firestore.)
export async function teardown() {
  const PushNotifications = await getPlugin();
  if (!PushNotifications) return;
  try { await PushNotifications.removeAllListeners(); } catch { /* noop */ }
}

// Attach a tap handler: when the user opens a notification carrying a movieId
// in its data payload, onOpenMovie(movieId) is invoked. Returns a detach fn.
export async function attachTapHandler(onOpenMovie) {
  const PushNotifications = await getPlugin();
  if (!PushNotifications) return () => {};
  const handle = await PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action) => {
      const movieId = action?.notification?.data?.movieId;
      if (movieId && onOpenMovie) onOpenMovie(String(movieId));
    },
  );
  return () => { try { handle.remove(); } catch { /* noop */ } };
}
